/**
 * Fetch wrapper that:
 *  - sends cookies on every request (credentials: 'include')
 *  - reads the eaa-csrf cookie and attaches X-CSRF-Token on mutations
 *  - primes the CSRF cookie via GET /api/health/live on first call if missing
 *  - throws a typed ApiError with HTTP status + parsed body on non-2xx
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly title: string,
    public readonly detail: string,
    public readonly errors?: Record<string, string[]>,
    /** Optional machine-readable code from ProblemDetails extensions (e.g. "email_not_verified"). */
    public readonly errorCode?: string,
  ) {
    super(detail || title);
  }
}

const CSRF_COOKIE = 'eaa-csrf';
const CSRF_HEADER = 'X-CSRF-Token';

function getCookie(name: string): string | undefined {
  const match = document.cookie
    .split('; ')
    .find(c => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : undefined;
}

let csrfPrimed = false;
async function ensureCsrfCookie() {
  if (csrfPrimed || getCookie(CSRF_COOKIE)) {
    csrfPrimed = true;
    return;
  }
  await fetch('/api/health/live', { credentials: 'include' });
  csrfPrimed = true;
}

type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Extra headers (e.g. X-Scan-Access-Token for anonymous-scan endpoints). */
  headers?: Record<string, string>;
};

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const isMutation = method !== 'GET';

  if (isMutation) {
    await ensureCsrfCookie();
  }

  const headers: Record<string, string> = {};
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (isMutation) {
    const csrfToken = getCookie(CSRF_COOKIE);
    if (csrfToken) headers[CSRF_HEADER] = csrfToken;
  }
  // Caller-supplied headers (e.g. X-Scan-Access-Token) override the defaults above.
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) headers[k] = v;
  }

  const response = await fetch(path, {
    method,
    credentials: 'include',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json') || contentType.includes('application/problem+json');

  if (!response.ok) {
    let title = response.statusText;
    let detail = '';
    let errors: Record<string, string[]> | undefined;
    let errorCode: string | undefined;
    if (isJson) {
      try {
        const body = await response.json();
        title = body.title ?? title;
        detail = body.detail ?? '';
        errors = body.errors;
        errorCode = typeof body.errorCode === 'string' ? body.errorCode : undefined;
      } catch {
        /* ignore parse error */
      }
    }
    throw new ApiError(response.status, title, detail, errors, errorCode);
  }

  if (!isJson) return undefined as T;
  return (await response.json()) as T;
}
